import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { axiosget, axiosput } from "../../helpers/httprequest";
import swal from "../../helpers/swal";
import PageTitle from "../../components/PageTitle";
import { Card } from "../../components/ui/Card";
import { Skeleton } from "../../components/ui/Skeleton";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/Alert";

const ProfileEdit = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const { isLoading, error } = useQuery({
    queryKey: ["studentProfileForEdit"],
    queryFn: async () => {
      const req = await axiosget("ms_students/profile");
      if (req?.type === "error") {
        throw new Error(req?.message || "Failed to load profile");
      }
      const student = req?.data;
      setForm({
        phonenumber: student?.phonenumber || "",
        alternatephone: student?.alternatephone || "",
        address: student?.address || "",
        lgaofresidence: student?.lgaofresidence || "",
        stateofresidence: student?.stateofresidence || "",
        emailaddress: student?.emailaddress || "",
        emergencycontact: student?.emergencycontact || "",
        emergencyphone: student?.emergencyphone || "",
      });
      return student;
    },
  });

  const _handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const _handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const req = await axiosput("ms_students/profile", form);
      if (req?.status !== 200) {
        throw new Error("Sorry, system could not finish processing your request");
      }
      const { type, message } = req.data;
      if (type === "success") {
        swal(type, message);
        setTimeout(() => navigate("/app/dashboard"), 1500);
      } else {
        swal(type || "warning", message);
      }
    } catch ({ message }) {
      swal("warning", message);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || form === null) {
    return (
      <div>
        <PageTitle title="Edit Profile" sub="Update your contact and personal information" />
        <Card className="mt-4">
          <div className="space-y-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageTitle title="Edit Profile" sub="Update your contact and personal information" />
        <Alert variant="danger" className="mt-4">
          <AlertTitle>Failed to load profile</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const fields = [
    { name: "phonenumber", label: "Phone Number", type: "text", placeholder: "e.g. 2348012345678" },
    { name: "alternatephone", label: "Alternate Phone", type: "text", placeholder: "e.g. 2348098765432" },
    { name: "emailaddress", label: "Email Address", type: "email", placeholder: "your@email.com" },
    { name: "address", label: "Home Address", type: "text", placeholder: "Street, City" },
    { name: "lgaofresidence", label: "LGA of Residence", type: "text", placeholder: "Local Government Area" },
    { name: "stateofresidence", label: "State of Residence", type: "text", placeholder: "State" },
    { name: "emergencycontact", label: "Emergency Contact Name", type: "text", placeholder: "Full name of contact" },
    { name: "emergencyphone", label: "Emergency Contact Phone", type: "text", placeholder: "e.g. 2348012345678" },
  ];

  return (
    <div>
      <PageTitle title="Edit Profile" sub="Update your contact and personal information" />

      <Card className="mt-4">
        <form onSubmit={_handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((field) => (
              <div className="form-group" key={field.name}>
                <label className="mb-1 font-weight-bold text-sm text-neutral-700" htmlFor={field.name}>
                  {field.label}
                </label>
                <input
                  id={field.name}
                  name={field.name}
                  type={field.type}
                  className="form-control py-2"
                  placeholder={field.placeholder}
                  value={form[field.name]}
                  onChange={_handleChange}
                />
              </div>
            ))}
          </div>

          <div className="mt-6 flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary py-2 px-5 font-weight-bold"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary py-2 px-5"
              onClick={() => navigate("/app/dashboard")}
            >
              Cancel
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ProfileEdit;
